/**
 * Volume + OBV Breakout Scanner service (Investment Ideas -- screening only).
 * Read-only: talks to `/api/v1/volume-obv-breakout`. The admin run endpoint is
 * gated server-side (`require_roles("ADMIN")`) and is not called from the UI.
 */
import api from "@/services/api";
import type {
  VolumeObvBreakoutListResponse,
  VolumeObvReadinessResponse,
  VolumeObvScanJobStatus,
  VolumeObvScanStatusResponse,
} from "@/types";

export interface VolumeObvBreakoutListParams {
  signal_date?: string;
  status?: string[];
  market_cap?: "LARGE_CAP" | "MID_CAP" | "SMALL_CAP" | "MICRO_CAP";
  min_rvol?: number;
  max_rvol?: number;
  obv_confirmed?: boolean;
  liquidity_ok?: boolean;
  review_required?: boolean;
  max_age_sessions?: number;
  search?: string;
  page?: number;
  page_size?: number;
  sort_by?: "symbol" | "rvol" | "pct_above_level" | "breakout_date" | "status" | "close_position";
  sort_order?: "asc" | "desc";
}

const volumeObvBreakoutService = {
  async list(params: VolumeObvBreakoutListParams = {}): Promise<VolumeObvBreakoutListResponse> {
    const response = await api.get<VolumeObvBreakoutListResponse>("/volume-obv-breakout", {
      params,
      // FastAPI expects repeated `status=A&status=B`, not `status[]=A`.
      paramsSerializer: { indexes: null },
    });
    return response.data;
  },

  // ---- Admin (Data Operations page); every route is ADMIN-only server-side ----
  async getReadiness(): Promise<VolumeObvReadinessResponse> {
    const response = await api.get<VolumeObvReadinessResponse>("/volume-obv-breakout/admin/readiness");
    return response.data;
  },

  async getScanStatus(): Promise<VolumeObvScanStatusResponse> {
    const response = await api.get<VolumeObvScanStatusResponse>("/volume-obv-breakout/admin/status");
    return response.data;
  },

  /** Starts a background scan. Omit every field for the latest completed
   * session; `as_of_date` for one session; `start_date`+`end_date` to backfill. */
  async startScan(
    body: { as_of_date?: string; start_date?: string; end_date?: string } = {}
  ): Promise<VolumeObvScanJobStatus> {
    const response = await api.post<VolumeObvScanJobStatus>("/volume-obv-breakout/admin/run-job", body);
    return response.data;
  },
};

export default volumeObvBreakoutService;
